import { useEffect, useState } from "react";
import { apiRequest } from "../../services/api";
const input = "mt-1 min-h-10 w-full rounded-lg border bg-white p-2 text-sm",
  button =
    "rounded-lg border px-3 py-2 text-sm font-semibold disabled:opacity-40";
export default function SettlementAllocations({
  data,
  companyId,
  accessToken,
  canWrite,
  busy,
  run,
  requestKey,
  onSaved,
  onBegin,
}) {
  const [evidence, setEvidence] = useState(""),
    [search, setSearch] = useState(""),
    [results, setResults] = useState([]),
    [payment, setPayment] = useState(""),
    [amount, setAmount] = useState(""),
    [lines, setLines] = useState([]),
    [note, setNote] = useState(""),
    [preview, setPreview] = useState(null),
    [confirmed, setConfirmed] = useState(false);
  useEffect(() => {
    setEvidence("");
    setLines([]);
    setResults([]);
    setPreview(null);
    setConfirmed(false);
  }, [data]);
  const url = `/accounting/${companyId}/settlement-evidence`;
  function invalidate() {
    setPreview(null);
    setConfirmed(false);
  }
  function choose(id) {
    onBegin?.();
    setEvidence(id);
    setSearch("");
    setResults([]);
    setPayment("");
    setAmount("");
    setLines([]);
    invalidate();
  }
  function find(e) {
    e.preventDefault();
    run(
      () =>
        apiRequest(
          `${url}/${evidence}/candidates?search=${encodeURIComponent(search.trim())}`,
          { accessToken },
        ),
      (r) => {
        setResults(r.rows);
        setPayment("");
        invalidate();
      },
    );
  }
  function add() {
    const p = results.find((r) => r.paymentId === payment);
    if (
      !p ||
      !/^[0-9]{1,12}(\.[0-9]{1,2})?$/.test(amount) ||
      !/[1-9]/.test(amount) ||
      lines.some((l) => l.paymentId === p.paymentId) ||
      lines.length >= 50
    )
      return;
    setLines([...lines, { ...p, amount }]);
    setPayment("");
    setAmount("");
    invalidate();
  }
  function review(e) {
    e.preventDefault();
    const body = {
      note,
      lines: lines.map((l) => ({ paymentId: l.paymentId, amount: l.amount })),
    };
    run(
      () =>
        apiRequest(`${url}/${evidence}/allocations/preview`, {
          accessToken,
          method: "POST",
          body: {
            ...body,
            requestKey: requestKey("allocation-batch:" + evidence, body),
          },
        }),
      (r) => {
        setPreview(r);
        setConfirmed(false);
      },
    );
  }
  function save() {
    if (!preview || !confirmed || busy) return;
    const body = {
      requestKey: preview.payload.requestKey,
      note: preview.payload.note,
      lines: preview.payload.lines.map((l) => ({
        paymentId: l.paymentId,
        amount: l.amount,
      })),
      previewHash: preview.previewHash,
      evidenceReviewed: confirmed,
    };
    run(
      async () => {
        await apiRequest(`${url}/${evidence}/allocations`, {
          accessToken,
          method: "POST",
          body,
        });
        return apiRequest(url, { accessToken });
      },
      (r) => {
        onSaved(r);
        setPreview(null);
        setConfirmed(false);
      },
    );
  }
  return (
    <section className="space-y-4 rounded-xl border border-blue-200 p-4">
      <h3 className="font-semibold">Batch & partial allocation review</h3>
      <p className="text-sm text-slate-600">
        Allocate gross amounts, not bank net, to original payments. Same
        company/GST/store/channel/method only. Exact fee/net shares come from
        the remaining evidence pool; no bank verification or financial posting.
      </p>
      <label className="block text-sm">
        Bank-credit evidence to allocate
        <select
          className={input}
          value={evidence}
          disabled={busy}
          onChange={(e) => choose(e.target.value)}
        >
          <option value="">Select active evidence</option>
          {data.evidence
            .filter(
              (e) =>
                !e.supersededId &&
                !data.channels.find((c) => c.id === e.channelId)?.retiredId &&
                (e.remainingGross ?? (e.match ? "0.00" : e.gross)) !== "0.00",
            )
            .map((e) => (
              <option value={e.id} key={e.id}>
                {e.bankCreditReference} · {e.externalTransactionId} · remaining
                gross ₹{e.remainingGross ?? e.gross}
              </option>
            ))}
        </select>
      </label>
      {evidence && (
        <>
          <form className="flex items-end gap-3" onSubmit={find}>
            <label className="flex-1 text-sm">
              Allocation receipt search
              <input
                className={input}
                required
                minLength={2}
                maxLength={80}
                value={search}
                disabled={busy}
                onChange={(e) => {
                  setSearch(e.target.value);
                  invalidate();
                }}
              />
            </label>
            <button className={button} disabled={busy}>
              Search allocation payments
            </button>
          </form>
          <div className="grid items-end gap-3 md:grid-cols-3">
            <label className="text-sm">
              Payment to add
              <select
                className={input}
                value={payment}
                disabled={busy}
                onChange={(e) => {
                  setPayment(e.target.value);
                  invalidate();
                }}
              >
                <option value="">Select original payment</option>
                {results.map((p) => (
                  <option
                    value={p.paymentId}
                    key={p.paymentId}
                    disabled={p.paymentRemaining === "0.00"}
                  >
                    {p.receiptNumber} · {p.method} · remaining ₹
                    {p.paymentRemaining ?? p.amount}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              Gross allocation amount ₹
              <input
                className={input}
                inputMode="decimal"
                value={amount}
                disabled={busy}
                onChange={(e) => {
                  setAmount(e.target.value);
                  invalidate();
                }}
              />
            </label>
            <button
              className={button}
              type="button"
              disabled={
                busy ||
                !payment ||
                !amount ||
                lines.some((l) => l.paymentId === payment) ||
                lines.length >= 50
              }
              onClick={add}
            >
              Add payment allocation
            </button>
          </div>
          <ul className="space-y-2">
            {lines.map((l) => (
              <li key={l.paymentId} className="flex items-center gap-3">
                <span>
                  {l.receiptNumber} · {l.method} · gross ₹{l.amount}
                </span>
                <button
                  className={button}
                  disabled={busy}
                  onClick={() => {
                    setLines(lines.filter((x) => x.paymentId !== l.paymentId));
                    invalidate();
                  }}
                >
                  Remove {l.receiptNumber} allocation
                </button>
              </li>
            ))}
          </ul>
          <form className="space-y-3" onSubmit={review}>
            <label className="block text-sm">
              Allocation review note
              <textarea
                className={input}
                required
                minLength={20}
                maxLength={1000}
                value={note}
                disabled={busy}
                onChange={(e) => {
                  setNote(e.target.value);
                  invalidate();
                }}
              />
            </label>
            <button
              className={button}
              disabled={busy || !lines.length || note.trim().length < 20}
            >
              Preview batch allocations — no changes
            </button>
          </form>
          {preview && (
            <div className="space-y-3 rounded-lg bg-slate-50 p-3">
              <p>{preview.notice}</p>
              <table className="w-full text-left">
                <thead>
                  <tr>
                    <th>Original payment</th>
                    <th>Gross ₹</th>
                    <th>Fees ₹</th>
                    <th>Fee tax ₹</th>
                    <th>Net share ₹</th>
                    <th>Payment remaining ₹</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.payload.lines.map((l) => (
                    <tr key={l.paymentId}>
                      <td>{l.payload.receiptNumber}</td>
                      <td>{l.payload.allocation.gross}</td>
                      <td>{l.payload.allocation.fees}</td>
                      <td>{l.payload.allocation.feeTax}</td>
                      <td>{l.payload.allocation.net}</td>
                      <td>{l.payload.paymentRemainingAfter}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p>
                Bank-credit remaining gross ₹
                {preview.payload.lines.at(-1).payload.remainingAfter.gross} ·
                remaining net ₹
                {preview.payload.lines.at(-1).payload.remainingAfter.net}
              </p>
              {canWrite && (
                <>
                  <label className="block text-sm">
                    <input
                      type="checkbox"
                      checked={confirmed}
                      disabled={busy}
                      onChange={(e) => setConfirmed(e.target.checked)}
                    />{" "}
                    I compared all selected gross allocations and fee/net shares
                    with retained provider and bank evidence.
                  </label>
                  <button
                    className={button}
                    disabled={busy || !confirmed}
                    onClick={save}
                  >
                    Record reviewed batch allocations — no posting
                  </button>
                </>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
